import moment from 'moment';
import { BILL_ALLOCATION_STATE } from 'src/api/billAutomation/bills';
import { getUtilityKinds } from './BillDetails/resolveAllocationTargets';
const DASH = '—';
const TABLE_DATE_FORMAT = 'DD/MM/YY';

/** Where a bill entered the system, keyed by the service's BillSource values. */
export const BILL_SOURCES = {
  api: 'API',
  billsDashboard: 'Bills Dashboard',
  email: 'Email',
  integration: 'Integration',
  propertyAllocation: 'Property Allocation',
  scrape: 'Utility Portal'
};
const BILL_SOURCE_BY_API_VALUE = {
  Api: BILL_SOURCES.api,
  Email: BILL_SOURCES.email,
  Integration: BILL_SOURCES.integration,
  Manual: BILL_SOURCES.propertyAllocation,
  Portal: BILL_SOURCES.billsDashboard,
  Scrape: BILL_SOURCES.scrape
};

/** Table labels for the service's BillStatus values, in pipeline order. */
export const BILL_STATUSES = {
  pending: 'Pending',
  processing: 'Processing',
  extracted: 'Extracted',
  validated: 'Validated',
  needsProperty: 'Needs property',
  failed: 'Failed'
};
const BILL_STATUS_BY_API_VALUE = {
  Pending: BILL_STATUSES.pending,
  Processing: BILL_STATUSES.processing,
  Extracted: BILL_STATUSES.extracted,
  Validated: BILL_STATUSES.validated,
  NeedsProperty: BILL_STATUSES.needsProperty,
  Failed: BILL_STATUSES.failed
};

/** The icon each utility is drawn with in the utilities column. */
const UTILITY_ICON_BY_KIND = {
  Electricity: 'electricity',
  Gas: 'natural-gas',
  Landscaping: 'irrigation',
  Sewer: 'sewer',
  Trash: 'trash',
  Water: 'water'
};

/** Anything the bill charges for that is none of the utilities above, stormwater included. */
const OTHER_UTILITY_ICON = 'meter';
const UTILITY_LABEL_BY_ICON = {
  electricity: 'Electricity',
  irrigation: 'Irrigation',
  meter: 'Other',
  'natural-gas': 'Gas',
  property: 'Property',
  sewer: 'Sewer',
  solar: 'Solar',
  steam: 'Steam',
  trash: 'Trash',
  water: 'Water',
  'water/sewer': 'Water/Sewer'
};

/**
 * The utilities column is icon-only, so the filter labels are derived from those icons. Going
 * through the icon keeps a bill's own wording for a service out of the filter's vocabulary.
 */
export function getUtilityLabel(iconType) {
  return UTILITY_LABEL_BY_ICON[iconType] || 'Other';
}

/**
 * The icons for the utilities a bill charges for. The bill names its services in its own words
 * ("electric", "Refuse Collection"), so they are read for the utility they name — the same reading
 * the allocation suggestion matches a property's utilities with. One utility billed twice, on two
 * properties or as two line groups, is one icon.
 */
export const getBillUtilityIcons = utilities => [...new Set((utilities || []).flatMap(utility => {
  const kinds = getUtilityKinds(utility === null || utility === void 0 ? void 0 : utility.serviceType);
  return kinds.length ? kinds.map(kind => UTILITY_ICON_BY_KIND[kind] || OTHER_UTILITY_ICON) : [OTHER_UTILITY_ICON];
}))];

/**
 * The states in which a service already has an allocation bill behind it — approved, held for
 * review, or still being calculated. All three live in the allocation service, which is why a bill
 * with any of them is no longer the dashboard's to delete.
 */
const ALLOCATED_STATES = [BILL_ALLOCATION_STATE.allocating, BILL_ALLOCATION_STATE.awaitingApproval, BILL_ALLOCATION_STATE.needsReview, BILL_ALLOCATION_STATE.allocated, BILL_ALLOCATION_STATE.skipped];

/** The services of a bill that have been allocated. A service refused outright is not one of them. */
export const getAllocatedServices = utilities => (utilities || []).filter(utility => ALLOCATED_STATES.includes(utility === null || utility === void 0 ? void 0 : utility.allocationState));
export const formatTableDate = value => {
  if (!value) return DASH;
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format(TABLE_DATE_FORMAT) : DASH;
};

/**
 * Both ends of the period are billed, so 1–31 March is 31 days and not 30. Times of day are dropped
 * first, otherwise a period stamped at different hours loses or gains a day.
 */
export const getServiceDayCount = (start, end) => {
  if (!(start !== null && start !== void 0 && start.isValid()) || !(end !== null && end !== void 0 && end.isValid())) return DASH;
  return Math.max(end.clone().startOf('day').diff(start.clone().startOf('day'), 'days') + 1, 1);
};

/** The address each property is filed under, for reading a bill's property ids back as addresses. */
export const getPropertyAddressIndex = properties => Object.fromEntries((properties || []).map(property => [property.propertyId, property.propertyAddress || property.propertyName || '']));

/**
 * Maps a bill from the company bills endpoint onto the shape the table renders. `addressById` names
 * the company's properties, so a bill matched to one is shown under the address it is filed under
 * rather than the address its provider printed — the two are rarely written the same way.
 */
export const mapCompanyBillToRow = (bill, addressById) => {
  var _bill$allocation$done, _bill$allocation, _bill$allocation$done2, _bill$allocation2, _bill$allocation$tota, _bill$allocation3, _bill$appfolioPush$do, _bill$appfolioPush, _bill$appfolioPush$to, _bill$appfolioPush2;
  const serviceStart = bill.serviceStartDate ? moment(bill.serviceStartDate) : null;
  const serviceEnd = bill.serviceEndDate ? moment(bill.serviceEndDate) : null;
  const hasServicePeriod = (serviceStart === null || serviceStart === void 0 ? void 0 : serviceStart.isValid()) && (serviceEnd === null || serviceEnd === void 0 ? void 0 : serviceEnd.isValid());
  const utilityIcons = getBillUtilityIcons(bill.utilities);
  const propertyIds = bill.propertyIds || [];
  const filedAddresses = propertyIds.map(propertyId => addressById === null || addressById === void 0 ? void 0 : addressById[propertyId]).filter(Boolean);
  const allocated = getAllocatedServices(bill.utilities);
  return {
    id: bill.billId,
    /**
     * A bill is the dashboard's to delete until one of its services is allocated, in part or in
     * full. The service refuses either way; this only saves offering an action it would turn down.
     */
    canDelete: allocated.length === 0 && ((_bill$allocation$done = (_bill$allocation = bill.allocation) === null || _bill$allocation === void 0 ? void 0 : _bill$allocation.done) !== null && _bill$allocation$done !== void 0 ? _bill$allocation$done : 0) === 0,
    fileName: bill.fileName || DASH,
    status: BILL_STATUS_BY_API_VALUE[bill.extractStatus] || bill.extractStatus || BILL_STATUSES.pending,
    statusTooltip: bill.extractStatus || '',
    billSource: BILL_SOURCE_BY_API_VALUE[bill.source] || bill.source || DASH,
    allocation: {
      done: (_bill$allocation$done2 = (_bill$allocation2 = bill.allocation) === null || _bill$allocation2 === void 0 ? void 0 : _bill$allocation2.done) !== null && _bill$allocation$done2 !== void 0 ? _bill$allocation$done2 : 0,
      total: (_bill$allocation$tota = (_bill$allocation3 = bill.allocation) === null || _bill$allocation3 === void 0 ? void 0 : _bill$allocation3.total) !== null && _bill$allocation$tota !== void 0 ? _bill$allocation$tota : 0
    },
    appfolioPush: {
      done: (_bill$appfolioPush$do = (_bill$appfolioPush = bill.appfolioPush) === null || _bill$appfolioPush === void 0 ? void 0 : _bill$appfolioPush.done) !== null && _bill$appfolioPush$do !== void 0 ? _bill$appfolioPush$do : 0,
      total: (_bill$appfolioPush$to = (_bill$appfolioPush2 = bill.appfolioPush) === null || _bill$appfolioPush2 === void 0 ? void 0 : _bill$appfolioPush2.total) !== null && _bill$appfolioPush$to !== void 0 ? _bill$appfolioPush$to : 0
    },
    utilityIcons,
    utilityTypes: utilityIcons.map(getUtilityLabel),
    /** What the bill charges for, with the utility each was matched to, for the details view. */
    utilities: bill.utilities || [],
    /** Every property the bill is for, as the company knows them. */
    propertyIds,
    // A bill nobody has matched to a property yet has only what its provider printed to show.
    propertiesLines: filedAddresses.length ? filedAddresses : bill.properties || [],
    account: bill.accountNumber || DASH,
    provider: bill.provider || DASH,
    servicePeriod: hasServicePeriod ? "".concat(serviceStart.format(TABLE_DATE_FORMAT), " - ").concat(serviceEnd.format(TABLE_DATE_FORMAT)) : DASH,
    days: getServiceDayCount(serviceStart, serviceEnd),
    amount: typeof bill.amount === 'number' ? bill.amount : null,
    invoiceNumber: bill.invoiceNumber || DASH,
    dueDate: formatTableDate(bill.dueDate),
    dateUploaded: formatTableDate(bill.acquiredAt)
  };
};